import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useField, useFormikContext } from "formik";
import { TextField, TextArea, ImageUpload, SelectField } from "../controllers/addDemandcontrollers";
import styles from "./demandForms.module.css";
import IMAGES from "../../../../../assets/images/images";
import { FieldWrapper } from "../../../../../components/Section/Section";
import { FormFieldLabel as Label, FormErrorLabel as ErrorLabel } from "../../../../../components/label/Labels";
import Asterisk from "../../../../../components/asterisk/Asterisk";
import { TextInput } from "../../../../../components/input/inputs";
import { connectivityServiceType, directionOption } from "../demandHelper";
import { UPDATE_VOLTE } from "../../../../../utils/constants/serviceConstants";

const CustomSearchInput = ({ label, suggestions = [], onValueChange, filteredSuggestions, setFilteredSuggestions, ...props }) => {

  const [field, meta] = useField(props);
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const hasError = meta.touched && meta.error;
  const inputClassName = hasError ? `${styles.input} ${styles.inputError}` : styles.input;

  const handleInputChange = useCallback(
    (e) => {
      const value = (e.target.value || "").toUpperCase().trim();
      setFieldValue(field.name, value);

      if (!value) {
        setFilteredSuggestions([]);
      } else {
        const lower = value.toLowerCase();
        setFilteredSuggestions(
          (suggestions || []).filter((item) =>
            item?.tadigId?.toLowerCase().includes(lower)
          )
        );
      }
      onValueChange?.(value, false);
    },
    [field.name, setFieldValue, suggestions, onValueChange]
  );

  const handleSuggestionClick = useCallback(
    (item) => {
      if (!item?.tadigId) return;

      setFieldValue(field.name, item.tadigId);
      setFieldTouched?.(field.name, true, true);

      setFilteredSuggestions([]);

      onValueChange?.(item, true);
    },
    [field.name, setFieldTouched, setFieldValue, onValueChange]
  );

  return (
    <FieldWrapper>
      <div>
        <Label text={label} /><Asterisk />
      </div>
      <div className={styles.parent}>
        <TextInput {...field} {...props} style={inputClassName} onChange={handleInputChange} />
        <img className={styles.serchicon} src={IMAGES.searchicon} alt="" />
      </div>
      {hasError && <ErrorLabel text={meta.error} />}

      {filteredSuggestions.length > 0 && (
        <div>
          <div className={styles.tablemain}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr className={styles.tr}>
                  <th className={styles.th}>TADIG Code</th>
                  <th className={styles.th}>Operator Name</th>
                  <th className={styles.th}>Country</th>
                </tr>
              </thead>
              <tbody className={styles.tbody}>
                {filteredSuggestions.map((item, idx) => (
                  <tr
                    className={styles.tr}
                    key={`${item?.tadigId || "row"}-${idx}`}
                    onClick={() => handleSuggestionClick(item)}
                  >
                    <td className={styles.td}>{item.tadigId}</td>
                    <td className={styles.td}>{item.roamingPartnerName}</td>
                    <td className={styles.td}>{item.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </FieldWrapper>
  );
};

const DestinationOpeningFields = React.memo(({ index,mode,serviceDetail, convertedCountries, roamingPartnerName,setNewRoamingPartner,isNewRoamingPartner, ...props }) => {

  const { values, setFieldValue } = useFormikContext();
  const [isExistingRomingPartner, setIsExistingRomingPartner] = useState(false);
  const row = values?.info?.[index] || {};
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const tadigTyped = (row?.roamingPartnerTadigId || "").toString().trim();
  const existsInSuggestions = useMemo(() => {
    if (!tadigTyped) return false;
    const upper = tadigTyped.toUpperCase();
    return (roamingPartnerName || []).some(
      (s) => s?.tadigId?.toUpperCase() === upper
    );
  }, [tadigTyped, roamingPartnerName]);

  const handleContinueClick = useCallback(() => {
    setNewRoamingPartner(true);
    setIsExistingRomingPartner(false);

    setFieldValue(`info.${index}.roamingPartnerName`, "");
    setFieldValue(`info.${index}.country`, "");
    setFieldValue(`info.${index}.connectivityServiceType`, "");
    setFieldValue(`info.${index}.direction`, "");
  }, [index, setFieldValue]);

  const handleSearchChange = useCallback(
    (valueOrItem, isFromSuggestions) => {
      if (isFromSuggestions && typeof valueOrItem === "object") {
        const { tadigId, roamingPartnerName: rpName, country } = valueOrItem || {};
        setIsExistingRomingPartner(true);
        setNewRoamingPartner(false);
        setFieldValue(`info.${index}.roamingPartnerTadigId`, tadigId || "");
        setFieldValue(`info.${index}.roamingPartnerName`, rpName || "");
        setFieldValue(`info.${index}.country`, country || "");
      } else {
        const typed = (valueOrItem || "").toString();
        setIsExistingRomingPartner(false);
        setNewRoamingPartner(false);
        setFieldValue(`info.${index}.roamingPartnerTadigId`, typed);
        setFieldValue(`info.${index}.roamingPartnerName`, "");
        setFieldValue(`info.${index}.country`, "");
      }
    },
    [index, setFieldValue]
  );
  useEffect(() => {
    if (mode==UPDATE_VOLTE) {
      if (serviceDetail?.services?.service_option?.isExistingRoamingPartner) {
        setIsExistingRomingPartner(true);
        setNewRoamingPartner(false)
      } else {
        setIsExistingRomingPartner(false);
        setNewRoamingPartner(true)
      }
    } else {
      setIsExistingRomingPartner(false);
      setNewRoamingPartner(false)
    }
  }, [mode])

  const showNotFoundPrompt =
    !isExistingRomingPartner &&
    !isNewRoamingPartner &&
    !!tadigTyped &&
    !existsInSuggestions &&
    filteredSuggestions.length === 0;

  return (
    <>
      <>
        <div className={styles.select}>
          <div className={`${styles.selectwidth} ${styles.dropdownwidths}`}>
            <TextField
              label="Requestor Organisation Name"
              name={`info.${index}.requestorOrganisationName`}
              type="text"
              placeholder="Requestor Organisation Name"
              disabled={mode == UPDATE_VOLTE || !values?.organization}
            />
          </div>
          <div className={styles.selectwidth}>
            <TextField
              label="Requestor TADIG Code"
              name={`info.${index}.requestorTadig`}
              type="text"
              placeholder="Requestor TADIG Code"
              disabled={!values?.organization}
            />
          </div>
        </div>
        <SelectField
          title={"Requestor Country"}
          options={convertedCountries}
          name={`info.${index}.requestorCountry`}
          onChange={() => { }}
          customStyles={{ width: "100%" }}
          isDisabled={!values?.organization}
        />

        <CustomSearchInput
          type="text"
          name={`info.${index}.roamingPartnerTadigId`}
          label="Roaming Partner TADIG Code"
          placeholder="Roaming Partner TADIG Code"
          suggestions={roamingPartnerName}
          onValueChange={handleSearchChange}
          disabled={!values?.organization}
          filteredSuggestions={filteredSuggestions}
          setFilteredSuggestions={setFilteredSuggestions}

        />
      </>
      {showNotFoundPrompt && (
        <>
          <div className={styles.continuetxt}>
            <span className={styles.highlight}>TADIG Code not found.</span>{" "}
            Would you like to continue to create new demand with the entered TADIG Code?
            <span className={styles.continuebtn} onClick={handleContinueClick}>
              Yes, Continue
            </span>
          </div>
        </>
      )}
      {(isExistingRomingPartner || isNewRoamingPartner) && (
        <>
          <TextField
            label="Roaming Partner Name"
            name={`info.${index}.roamingPartnerName`}
            type="text"
            disabled={isExistingRomingPartner}
            autoSelect={true}
            placeholder="Roaming Partner Name"
          />
          {isExistingRomingPartner && (
            <TextField
              label="Country"
              name={`info.${index}.country`}
              type="text"
              disabled
              placeholder="Country"
              autoSelect={true}
            />
          )}
          {isNewRoamingPartner && (
            <SelectField
              title={"Country"}
              name={`info.${index}.country`}
              options={convertedCountries}
              onChange={() => { }}
              customStyles={{ width: "100%" }}
            />
          )}
          <div className={styles.select}>
            <div className={`${styles.selectwidth} ${styles.dropdownwidths}`}>
              <SelectField
                title={"Connectivity Service Type"}
                name={`info.${index}.connectivityServiceType`}
                options={connectivityServiceType}
                onChange={() => { }}
                customStyles={{ width: "100%" }}
              />
            </div>
            <div className={styles.selectwidth}>
              <SelectField
                title={"Direction"}
                name={`info.${index}.direction`}
                options={directionOption}
                onChange={() => { }}
                customStyles={{ width: "100%" }}
              />
            </div>
          </div>
          <ImageUpload
            label="Attachment IR21"
            optional={false}
            name={`info.${index}.files`}
            value={values?.info?.[index]?.files}
            setFieldValue={setFieldValue}
            displayMessage={"Upload limit: 1 PDF file, up to 30 MB."}
            maxFileSize={30 * 1024 * 1024}
            maxFileSizeTXT={"30MB"}
            combineFilesMaxSize={30 * 1024 * 1024}
            combineFilesMaxSizeTXT={"Combined file size exceeds the 30MB limit"}
            numOfFilesAllowToUpload={1}
            errorMessageForNumberOfFiles={"You can upload max 1 files."}
            acceptFileType={{ "application/pdf": [".pdf"] }}
          />
        </>
      )}
    </>
  );
}
);

export default DestinationOpeningFields;
